import { useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * Categorie kiezen uit wat er al bestaat, of een nieuwe typen.
 *
 * Als vrij tekstveld levert dit onvermijdelijk bijna-duplicaten op: "Website &
 * Development" naast "Website en development". Daarom staan de bestaande
 * categorieën in een lijst en is een nieuwe aanmaken een expliciete keuze,
 * inclusief waarschuwing als er al iets bestaat dat alleen in schrijfwijze
 * verschilt.
 */
interface CategoryPickerProps {
  value: string;
  onChange: (value: string) => void;
  existing: string[];
  id?: string;
}

export function CategoryPicker({ value, onChange, existing, id }: CategoryPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const trimmed = value.trim();
  const needle = trimmed.toLowerCase();

  const matches = useMemo(
    () => existing.filter((entry) => entry.toLowerCase().includes(needle)),
    [existing, needle],
  );

  const exact = existing.find((entry) => entry.toLowerCase() === needle);
  const showCreate = Boolean(trimmed) && !exact;

  /** Bestaat er al een categorie die alleen in schrijfwijze verschilt? */
  const nearDuplicate = useMemo(() => {
    if (!showCreate) return null;
    const normalise = (entry: string) => entry.toLowerCase().replace(/[^a-z0-9]/g, '');
    const target = normalise(trimmed);
    return existing.find((entry) => normalise(entry) === target) ?? null;
  }, [existing, showCreate, trimmed]);

  const options = showCreate ? [...matches, `__new__${trimmed}`] : matches;

  const choose = (option: string) => {
    onChange(option.startsWith('__new__') ? option.slice('__new__'.length) : option);
    setOpen(false);
    inputRef.current?.blur();
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input
          id={id}
          ref={inputRef}
          value={value}
          placeholder="Kies of typ een categorie"
          onChange={(event) => {
            onChange(event.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onKeyDown={(event) => {
            if (!open || !options.length) return;
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setHighlight((current) => (current + 1) % options.length);
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setHighlight((current) => (current - 1 + options.length) % options.length);
            } else if (event.key === 'Enter') {
              event.preventDefault();
              choose(options[highlight]);
            } else if (event.key === 'Escape') {
              setOpen(false);
            }
          }}
          className="pr-8"
        />
        <button
          type="button"
          aria-label="Categorieën tonen"
          tabIndex={-1}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            setOpen((current) => !current);
            inputRef.current?.focus();
          }}
          className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>

      {nearDuplicate && (
        <p className="mt-1 text-[11px] text-amber-600">
          Er bestaat al “{nearDuplicate}”. Bedoelde je die?
        </p>
      )}

      {open && options.length > 0 && (
        <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border bg-popover shadow-md">
          {options.map((option, index) => {
            const isNew = option.startsWith('__new__');
            const label = isNew ? option.slice('__new__'.length) : option;
            return (
              <li key={option}>
                <button
                  type="button"
                  tabIndex={-1}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(option)}
                  onMouseEnter={() => setHighlight(index)}
                  className={cn(
                    'flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm',
                    index === highlight && 'bg-accent',
                  )}
                >
                  {isNew ? (
                    <>
                      <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span>
                        Nieuwe categorie <strong>{label}</strong>
                      </span>
                    </>
                  ) : (
                    <>
                      <Check
                        className={cn(
                          'h-3.5 w-3.5 shrink-0',
                          label === value ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className="truncate">{label}</span>
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default CategoryPicker;
